import {
  type BreakdownSelection,
  isRatingBreakdown,
  isSentimentBreakdown,
} from 'features/charts-v2/components/breakdown-selection-utils'
import {
  MAX_STACKING_CATEGORIES,
  RATING_COLORS,
  SENTIMENT_ELEMENT_COLORS,
  SentimentColorGroup,
  STACKING_OVERFLOW_LABEL,
} from 'features/charts-v2/constants'
import { chartConfig } from 'features/charts-v2/services/chart-config-service'
import { DEFAULT_SERIES_COLOR } from 'features/charts-v2/services/series-color-palette'
import type { Chart } from 'features/charts-v2/types'
import type { SortDirection } from 'features/charts-v2/types/chart-strategy'
import { ALIAS_FOR_NULL_VALUES } from 'src/constants/filter-values'

const SENTIMENT_VALUES = ['Positive', 'Neutral', 'Negative']
const SENTIMENT_VALUE_COLORS: Record<string, string> = {
  Positive: SENTIMENT_ELEMENT_COLORS[SentimentColorGroup.POSITIVE],
  Neutral: SENTIMENT_ELEMENT_COLORS[SentimentColorGroup.NEUTRAL],
  Negative: SENTIMENT_ELEMENT_COLORS[SentimentColorGroup.NEGATIVE],
}

const getRatingColor = (value: string): string => {
  const match = String(value).match(/^(\d+)/)
  if (match) {
    const n = parseInt(match[1]!, 10)
    if (n >= 1 && n <= 5) return RATING_COLORS[String(n)]!
  }
  return DEFAULT_SERIES_COLOR
}

/**
 * Format a raw rating value ("5") as a star label ("5 Stars") for display —
 * mirrors MetricColorLegend's rating legend so the breakdown legend isn't the
 * odd one out with a bare number. Non-numeric / out-of-range values pass through.
 */
export const getRatingStarLabel = (value: string): string => {
  const match = value.match(/^(\d+)/)
  if (!match) return value
  const n = parseInt(match[1]!, 10)
  if (n < 1 || n > 5) return value
  return `${n} Star${n > 1 ? 's' : ''}`
}

/**
 * Build an ordered list of { value, color } entries for a breakdown, mirroring
 * the color rules used by ChartLegend so the chart and legend stay in sync.
 *
 * - Sentiment breakdown: fixed three-value sentiment palette.
 * - Rating breakdown: 1–5 star red-to-green palette.
 * - Everything else: indexed primary palette in the order categories first
 *   appear in `chartData.data`.
 */
export function buildBreakdownLegendEntries(
  breakdown: BreakdownSelection,
  chartData: Chart | null,
  breakdownIndex: number = 0,
  sortDirection: SortDirection = 'DESC',
): { value: string; color: string }[] {
  if (!chartData) return []

  if (isSentimentBreakdown(breakdown)) {
    return SENTIMENT_VALUES.map((v) => ({
      value: v,
      color: SENTIMENT_VALUE_COLORS[v]!,
    }))
  }

  if (isRatingBreakdown(breakdown)) {
    // Sum record count per distinct rating value (mirrors the general branch and
    // LegendDataTable's null-breakdown guard) so the null-derived "Unknown" bucket
    // is omitted when it has zero records — a phantom outer-join row, not a real
    // rating value.
    const totals: Record<string, number> = {}
    for (const datum of chartData.data) {
      const raw = datum.category[breakdownIndex]
      const str = raw != null ? String(raw) : ALIAS_FOR_NULL_VALUES
      totals[str] = (totals[str] ?? 0) + (datum.values[0] ?? 0)
    }
    const ratingValue = (s: string): number => {
      const match = s.match(/^(\d+)/)
      return match ? parseInt(match[1]!, 10) : -1
    }
    return Object.keys(totals)
      .filter(
        (value) => value !== ALIAS_FOR_NULL_VALUES || (totals[value] ?? 0) > 0,
      )
      .map((value) => ({ value, color: getRatingColor(value) }))
      .sort((a, b) => ratingValue(b.value) - ratingValue(a.value))
  }

  // General case: sort by per-category total DESC (matching transformForOrderedStackedBar),
  // then assign palette colors by that sorted index so chart and legend stay in sync.
  const totals: Record<string, number> = {}
  for (const datum of chartData.data) {
    const raw = datum.category[breakdownIndex]
    const str = raw != null ? String(raw) : ALIAS_FOR_NULL_VALUES
    totals[str] = (totals[str] ?? 0) + (datum.values[0] ?? 0)
  }

  const sorted = Object.keys(totals).sort((a, b) => {
    const diff = (totals[a] ?? 0) - (totals[b] ?? 0)
    return sortDirection === 'ASC' ? diff : -diff
  })

  // For secondary breakdowns (stacking axis), mirror the chart's cap so the
  // legend only lists entries that are actually drawn as segments.
  if (breakdownIndex >= 1 && sorted.length > MAX_STACKING_CATEGORIES) {
    const top = sorted.slice(0, MAX_STACKING_CATEGORIES)
    return [
      ...top.map((value, index) => ({
        value,
        color: chartConfig.getColor('primary', index),
      })),
      {
        value: STACKING_OVERFLOW_LABEL,
        color: chartConfig.getColor('primary', MAX_STACKING_CATEGORIES),
      },
    ]
  }

  return sorted.map((value, index) => ({
    value,
    color: chartConfig.getColor('primary', index),
  }))
}

/**
 * True when the breakdown's colors are semantic and value-derived rather than
 * positional — sentiment (green/amber/red) and rating (red→green). These need no
 * stability reconciliation: the same value always yields the same color.
 */
export function hasFixedBreakdownPalette(
  breakdown: BreakdownSelection,
): boolean {
  return isSentimentBreakdown(breakdown) || isRatingBreakdown(breakdown)
}

/**
 * Build a Map<name, color> for use in chart strategy render methods.
 * Keyed by the datapoint name (= formatted category[0]).
 */
export function buildBreakdownColorMap(
  primaryBreakdown: BreakdownSelection,
  chartData: Chart | null,
): Map<string, string> {
  const entries = buildBreakdownLegendEntries(primaryBreakdown, chartData, 0)
  return new Map(entries.map(({ value, color }) => [value, color]))
}

/**
 * Merge a fresh color map into the persisted one, keeping existing assignments
 * stable and assigning the next available palette slot to any new value.
 *
 * - Values already in `previous` keep their color even if absent from `fresh`
 *   (so a re-included breakdown value restores its original color).
 * - Values in `fresh` but not in `previous` get the lowest-indexed palette color
 *   not already claimed by any entry in the merged map.
 * - When the palette is exhausted, wraps via modulo (same behaviour as the
 *   positional fallback in chart-config-service.ts).
 */
export function reconcileBreakdownColorMap(
  previous: Map<string, string>,
  fresh: Map<string, string>,
  palette: string[],
): Map<string, string> {
  // Fast path: if every value in fresh is already mapped, nothing changes.
  // Return the *same reference* so callers relying on reference equality
  // (React.memo, context value identity) are not needlessly invalidated.
  // This is the common case on hover, where data hasn't changed.
  const hasNewValues = [...fresh.keys()].some((v) => !previous.has(v))
  if (!hasNewValues) return previous

  const merged = new Map(previous)
  const usedColors = new Set(previous.values())

  for (const value of fresh.keys()) {
    if (merged.has(value)) continue

    // Find the first palette color not already in use
    let color: string | undefined
    for (const candidate of palette) {
      if (!usedColors.has(candidate)) {
        color = candidate
        break
      }
    }
    // All palette slots are taken — wrap around deterministically
    if (color === undefined) {
      color = palette[merged.size % palette.length]!
    }

    merged.set(value, color)
    usedColors.add(color)
  }

  return merged
}
